/**
 * The per-rung countdown ring.
 *
 * Driven by requestAnimationFrame writing straight to the DOM — no React state,
 * so a 60fps ring costs zero renders. It reads server time (invariant 2), which
 * is also why it keeps counting correctly across a reconnect: the deadline
 * never moved, only our socket did.
 */

import { useEffect, useRef } from 'react';

const RADIUS = 26;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export type RingTone = 'neutral' | 'urgent' | 'decision' | 'golden';

export interface CountdownRingProps {
  /** Server instant the window closes. Null renders an idle ring. */
  readonly deadlineServerTs: number | null;
  /** Full width of the window, so the arc can be a fraction of it. */
  readonly durationMs: number;
  readonly serverNow: () => number;
  readonly tone?: RingTone;
  readonly label?: string;
  readonly size?: number;
}

export function CountdownRing({
  deadlineServerTs,
  durationMs,
  serverNow,
  tone = 'neutral',
  label,
  size = 64,
}: CountdownRingProps): React.JSX.Element {
  const arcRef = useRef<SVGCircleElement | null>(null);
  const textRef = useRef<HTMLSpanElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let raf = 0;
    const frame = (): void => {
      raf = window.requestAnimationFrame(frame);
      const arc = arcRef.current;
      const text = textRef.current;
      const root = rootRef.current;
      if (!arc || !text || !root) return;

      if (deadlineServerTs === null || durationMs <= 0) {
        arc.style.strokeDashoffset = String(CIRCUMFERENCE);
        text.textContent = '';
        return;
      }
      const remaining = Math.max(0, deadlineServerTs - serverNow());
      const fraction = Math.min(1, remaining / durationMs);
      arc.style.strokeDashoffset = String(CIRCUMFERENCE * (1 - fraction));
      text.textContent = (remaining / 1000).toFixed(remaining < 10_000 ? 1 : 0);
      root.dataset['urgent'] = remaining < 3_000 ? 'true' : 'false';
    };
    raf = window.requestAnimationFrame(frame);
    return () => window.cancelAnimationFrame(raf);
  }, [deadlineServerTs, durationMs, serverNow]);

  return (
    <div
      ref={rootRef}
      className="gg-ring relative grid place-items-center"
      data-tone={tone}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 64 64" className="absolute inset-0 h-full w-full -rotate-90">
        <circle cx="32" cy="32" r={RADIUS} className="gg-ring-track" />
        <circle
          ref={arcRef}
          cx="32"
          cy="32"
          r={RADIUS}
          className="gg-ring-arc"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={0}
        />
      </svg>
      <div className="flex flex-col items-center leading-none">
        <span ref={textRef} className="gg-ring-value tabular-nums text-sm font-semibold" />
        {label ? (
          <span className="mt-0.5 text-[9px] uppercase tracking-widest opacity-60">{label}</span>
        ) : null}
      </div>
    </div>
  );
}
